/**
 * The print run, as a URL (PLAN-SCORESHEETS S3).
 *
 * `?batches=a,b,c` rather than router state, because the sheet has to survive a
 * reload and a bookmark: minting is idempotent, so the ids are the whole page
 * and everything else is derived from them. Comma-separated rather than a
 * repeated key so the address bar reads as the print run it is.
 *
 * `?blends=` rides beside it in exactly the same grammar, because a print run
 * is a list of beers and a beer is a batch or a blend (D-BS9). A blend's
 * Feedback page navigates here with one, and the sheet prints the batches in
 * their key's order and then the blends in theirs. `?journals=` is the third
 * key, in the same grammar again, since a journal became something a card is
 * printed for (D-JL6); its cards come after the blends'.
 *
 * The ids arrive from outside the app — a copied link, a hand-edited query, a
 * stale bookmark — so they are validated here rather than trusted: each one
 * must be a document id, which is the same pattern the batch documents enforce
 * and the same one the Worker's routes check before an id reaches an R2 key.
 * Anything else is dropped rather than raised, because a query with one bad id
 * in it should print the beers it did name.
 *
 * `?recipe=true` rides beside the run (D-TC1): the brewer's opt-in to letting
 * tasters see the recipe. It lives in the URL for the same reason the ids do —
 * the page re-mints on every load, and the flag is part of what it mints.
 */

import type { BatchDoc } from '@beerwright/commands';
import { documentIdSchema } from '@beerwright/commands';
import type { SearchSchemaInput } from '@tanstack/react-router';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { type PrintRunIds, TastingCards } from '@/components/batches/tasting-cards';

/** The print run this page draws. */
export interface TastingCardsSearch {
  /** Batch ids, in the order the cards are printed. */
  readonly batches: readonly BatchDoc['id'][];
  /**
   * Blend ids, in the order the cards are printed, after the batches (D-BS9).
   *
   * `undefined` rather than `[]` for an ordinary batch run, for the reason
   * `recipe` below states at length: the router stringifies every key this
   * function returns, so an empty array would print `?blends=` in the address
   * bar of every print run that never named one. @see the header
   */
  readonly blends: readonly string[] | undefined;
  /**
   * Journal ids, in the order the cards are printed, after the blends (D-JL6).
   *
   * `undefined` for a run that names none, for `blends`' reason exactly.
   */
  readonly journals: readonly string[] | undefined;
  /**
   * Whether the cards carry a link to the recipe (D-TC1).
   *
   * `true` or absent, never `false`, for the reason `lib/search-params.ts`
   * states at length: the router's default serializer stringifies every key
   * this function returns, so a `false` would print `?recipe=false` in the
   * address bar of every brewer who never touched the checkbox. Off is the
   * default and is not worth a character.
   */
  readonly recipe: true | undefined;
}

/**
 * The ids in one of the run's three keys, or an empty run.
 *
 * Deduplicated, because two cards for one bottle is a printing mistake rather
 * than a request, and the mint route would answer the second entry with the
 * first one's link anyway.
 *
 * **Both shapes are accepted, and that is what makes the page work at all.**
 * The dialog navigates here with the run as one comma-separated string, and the
 * router then writes what came *out* of this function back into the address bar
 * — as an array, which is how the ids arrive on the very next pass and on every
 * load after it. Reading only the string therefore emptied the run a moment
 * after the sheet opened, and printed "We could not find those" for a
 * print run that was perfectly good.
 */
function parseDocumentIds(value: unknown): readonly string[] {
  const parts =
    typeof value === 'string' ? value.split(',') : Array.isArray(value) ? (value as unknown[]) : [];
  const ids = parts
    .map((id) => (typeof id === 'string' ? id.trim() : ''))
    .filter((id) => documentIdSchema.safeParse(id).success);
  return [...new Set(ids)];
}

/**
 * A run with no blends (or no journals) in it, as an absent key rather than an
 * empty one.
 *
 * `batches` keeps its `[]`, because it is the run and a run of nothing is what
 * "We could not find those" is about. @see TastingCardsSearch.blends
 */
function emptyToUndefined(ids: readonly string[]): readonly string[] | undefined {
  return ids.length === 0 ? undefined : ids;
}

/**
 * `?recipe=` as the one flag this page carries (D-TC1).
 *
 * Three spellings of "on" are accepted and everything else is off: the real
 * boolean `true`, which is what a URL this app wrote itself decodes back to
 * once the router's `JSON.parse` has had it; `'1'`, which is the short form
 * the design named and the one a brewer is likeliest to type by hand; and
 * `'true'`, the string a bookmark from a parser that left the value alone
 * produces. A missing param, `'0'`, `'false'` and anything stranger all mean
 * the ordinary card, which shows no recipe at all (PLAN-SCORESHEETS S3).
 */
function parseRecipeFlag(value: unknown): true | undefined {
  return value === true || value === '1' || value === 'true' ? true : undefined;
}

/**
 * The blends or journals of a run that named none, as one array rather than a
 * fresh one.
 *
 * The sheet memoises its run on the ids it was handed, so a new `[]` on every
 * render would be a new run on every render — and a new run re-mints.
 */
const EMPTY_RUN: readonly string[] = [];

/**
 * A run grown on the sheet, as the three keys carry it (PLAN-UX-PASS D3, D-JL6).
 *
 * The write half of {@link parseDocumentIds}: one comma-separated string per
 * key, and a key nothing is under is left out rather than written empty, for the
 * reason `TastingCardsSearch.blends` gives at length. Exported so the round trip
 * — write, then validate — can be asserted in one place, because the two halves
 * disagreeing is how a sheet would empty itself a moment after a beer was added.
 */
export function tastingCardsRunSearch(run: PrintRunIds): {
  readonly batches: string | undefined;
  readonly blends: string | undefined;
  readonly journals: string | undefined;
} {
  return {
    batches: runKey(run.batches),
    blends: runKey(run.blends),
    journals: runKey(run.journals),
  };
}

function runKey(ids: readonly string[]): string | undefined {
  return ids.length === 0 ? undefined : ids.join(',');
}

export const Route = createFileRoute('/batches/tasting-cards')({
  /**
   * `SearchSchemaInput` on the parameter is what tells the router that what
   * goes *in* is not what comes out: a caller navigates here with the run as
   * one comma-separated string, which is what lands in the address bar, and
   * everything reading the search back gets the ids already split and checked.
   */
  validateSearch: (
    search: {
      readonly batches?: unknown;
      readonly blends?: unknown;
      readonly journals?: unknown;
      readonly recipe?: unknown;
    } & SearchSchemaInput,
  ): TastingCardsSearch => ({
    batches: parseDocumentIds(search.batches),
    blends: emptyToUndefined(parseDocumentIds(search.blends)),
    journals: emptyToUndefined(parseDocumentIds(search.journals)),
    recipe: parseRecipeFlag(search.recipe),
  }),
  component: TastingCardsPage,
});

function TastingCardsPage() {
  const { batches, blends, journals, recipe } = Route.useSearch();
  // Scoped, like `/gallery`: an unscoped `useNavigate` types `search` against
  // every registered route at once and cannot know this one's shape.
  const navigate = useNavigate({ from: Route.fullPath });
  return (
    <TastingCards
      batchIds={batches}
      // `undefined` is the ordinary run, which names no blend; the sheet takes
      // a list either way rather than learning that absence means empty.
      blendIds={blends ?? EMPTY_RUN}
      journalIds={journals ?? EMPTY_RUN}
      recipe={recipe === true}
      // A replace, not a push: ticking the box is a property of the sheet in
      // front of the brewer rather than a place they went, and Back is the way
      // out of this page. The run rides along unchanged — the ids are still the
      // whole print run, and re-minting with the flag flipped is exactly how
      // the cards pick the link up.
      onRecipeChange={(next) => {
        void navigate({
          replace: true,
          search: (prev) => ({ ...prev, recipe: next ? true : undefined }),
        });
      }}
      // A replace as well, and for the same reason the labels page replaces
      // every tap (PLAN-UX-PASS D3): adding a beer to the sheet in front of the
      // brewer is not a place they went, and Back is the way out of this page
      // rather than a way of undoing the last beer. `resetScroll` off because a
      // chip pressed below the toolbar must not throw the page to the top.
      onRunChange={(next) => {
        void navigate({
          replace: true,
          resetScroll: false,
          search: (prev) => ({ ...prev, ...tastingCardsRunSearch(next) }),
        });
      }}
      // The list the sheet resolved to, because a blender who printed cards
      // for a gueuze came from `/blending` and has no batch to go back to
      // (D-BS9), and a brewer who printed them for a mead came from `/journals`
      // (D-JL6). The page names the list; the route owns the navigation.
      onBack={(list) => {
        void navigate({
          to: list === 'blending' ? '/blending' : list === 'journals' ? '/journals' : '/batches',
        });
      }}
    />
  );
}
